import { useEffect, useCallback, useMemo, useSyncExternalStore } from 'react';
import { useStore } from '../store/store';
import {
  subscribeToSubscriptionStatus,
  getSubscriptionStatusSnapshot,
  loadSubscriptionStatus,
  ackSubscriptionNotification
} from './subscriptionStatusSource';
import { isoDay } from '../utils/contractPeriods';
import {
  getUsageColor,
  getUsagePercentage,
  getRemaining,
  isLimitReached,
  USAGE_COLORS,
  ENRICHMENT_AMBER_THRESHOLD
} from '../utils/constants/subscription';

/**
 * The day the allowances reset (TD-2813): the start of the next period, as the
 * backend reports it in `period.next_starts_on`.
 *
 * Usage counts in the current period until the next one starts, so this is the
 * honest date even after a period was ended early or an admin left a gap; the
 * day after `ends_on` would promise the reset too early then. Null while no
 * next period exists: the last period of a contract, or any contract before its
 * renewal has been created once the cancellation deadline passed (TD-2811).
 */
const resetDateOf = (p) => isoDay(p?.next_starts_on) || null;

/**
 * Hook to read subscription status data.
 * Provides download usage, token usage, and computed display values.
 *
 * Every instance reads ONE shared snapshot (see `subscriptionStatusSource`), so
 * a deduction refetched by any consumer is visible in all of them, and
 * concurrent mounts share a single request. The signature and the returned
 * shape are unchanged from the per-mount version.
 *
 * API contract (PR #615): nested { downloads: {used,limit,remaining}, tokens: {used,limit,remaining} }
 *
 * Enrichment contract (TD-2093, BE commit cc91ca7):
 *   BE shape: { limit, used, remaining, overage_enabled, period_end }
 * The entire `enrichment` key is OMITTED when the company's
 * enrichment_credit_limit IS NULL — its presence is the per-company gate.
 * `period_end` equals `subscription_expiry` (ISO date string).
 * `overage_enabled` is ignored on the FE per v3 (overage removed from product).
 * Color thresholds differ from downloads/tokens: amber when remaining < 20%
 * of limit (v3, was 10%), grey "on hold" at limitReached (was red).
 *
 * @param {Object} options
 * @param {boolean} [options.enabled=true] - Whether to load on mount. A
 *   disabled instance subscribes but never issues a request, and reports
 *   isLoading false — it is used by consumers whose parent already owns the
 *   figures, and by the ContactCard fan-out guard.
 * @returns {Object} Subscription status with computed usage values
 */
const useSubscriptionStatus = ({ enabled = true } = {}) => {
  const store = useStore();
  const snapshot = useSyncExternalStore(
    subscribeToSubscriptionStatus,
    getSubscriptionStatusSnapshot
  );
  const { data } = snapshot;

  // Subscribing does not load; only this gated effect does. The fan-out guard
  // depends on it: many cards mount the hook with enabled false and must issue
  // no request at all.
  useEffect(() => {
    if (enabled) {
      loadSubscriptionStatus(store);
    }
  }, [enabled, store]);

  // Resolves with the response payload (or null on error) so callers can do a
  // fresh pre-action check, e.g. `await refetch()`. Forced, because every
  // production refetch is a post-deduction reconcile: joining an open
  // background load would resolve with the pre-deduction balance.
  const refetch = useCallback(() => loadSubscriptionStatus(store, { force: true }), [store]);

  // TD-2443: acknowledge that the user has seen a period notification (fallback /
  // on-hold) so the BE suppresses it until the next period. Fire-and-forget from
  // the caller's perspective; resolves true on success, false on error.
  const ackNotification = useCallback((type) => ackSubscriptionNotification(store, type), [store]);

  // Loading is first-load-only: true until the first response settles, false
  // for every refetch after it. The agent token widget hard-returns null while
  // loading where the counters use the tolerant "loading and no data", so a
  // shared flag that went true on every refetch would blink the widget out of
  // existence app-wide on every reveal and every agent turn.
  const isLoading = enabled && !snapshot.hasLoaded;

  // Memoize computed download values to prevent unnecessary re-renders
  const downloads = useMemo(() => {
    const dl = data?.downloads;
    if (!dl) return null;
    return {
      used: dl.used ?? 0,
      limit: dl.limit ?? 0,
      // TD-2883: `limit ?? 0` collapses two different contracts — no download
      // allowance configured (BE sends null) and an allowance of zero (demo).
      // The agent usage panel must hide the former and show a 0 counter for the
      // latter, so keep the distinction the normalisation throws away.
      configured: dl.limit !== null && dl.limit !== undefined,
      color: getUsageColor(dl.used ?? 0, dl.limit ?? 0),
      percentage: getUsagePercentage(dl.used ?? 0, dl.limit ?? 0),
      remaining: Math.min(
        dl.remaining ?? getRemaining(dl.used ?? 0, dl.limit ?? 0),
        getRemaining(dl.used ?? 0, dl.limit ?? 0)
      ),
      limitReached: isLimitReached(dl.used ?? 0, dl.limit ?? 0)
    };
  }, [data]);

  // Memoize computed enrichment values. BE omits the `enrichment` block when
  // enrichment_credit_limit IS NULL — null return is the per-company gate that
  // useEnrichmentAccess reads.
  const enrichment = useMemo(() => {
    const en = data?.enrichment;
    if (!en) return null;
    const used = en.used ?? 0;
    const limit = en.limit ?? 0;
    const localRemaining = getRemaining(used, limit);
    const remaining = Math.min(en.remaining ?? localRemaining, localRemaining);
    const limitReached = isLimitReached(used, limit);
    let color = USAGE_COLORS.NORMAL;
    if (limitReached) {
      color = USAGE_COLORS.HOLD;
    } else if (limit > 0 && remaining / limit < ENRICHMENT_AMBER_THRESHOLD) {
      color = USAGE_COLORS.WARNING;
    }
    return {
      used,
      limit,
      remaining,
      color,
      limitReached,
      // TD-2883: same null-vs-zero distinction as downloads above.
      configured: en.limit !== null && en.limit !== undefined,
      periodEnd: en.period_end ?? null,
      // TD-2813: credits reset with the period, on the same day as downloads.
      resetsOn: resetDateOf(data?.period)
    };
  }, [data]);

  // TD-2813: which contract period these figures belong to.
  //
  // PRESENT for every subscribed company from the first deploy — TD-2807
  // backfills one period per company — so its presence says nothing about
  // whether this customer has more than one. Read `isMultiPeriod` for that. It
  // is null only for a company whose migration was skipped, and a consumer must
  // read that as "counting has not changed".
  const period = useMemo(() => {
    const p = data?.period;
    if (!p) return null;
    return {
      seq: p.seq ?? null,
      total: p.total ?? null,
      startsOn: p.starts_on ?? null,
      endsOn: p.ends_on ?? null,
      label: p.label ?? null,
      freeMonths: Number(p.free_months) || 0,
      resetsOn: resetDateOf(p),
      // Lifetime and per-period counting are the same thing while a contract has
      // only one period, so wording only changes once it has more.
      isMultiPeriod: (p.total ?? 1) > 1
    };
  }, [data]);

  // Memoize computed token values to prevent unnecessary re-renders
  const tokens = useMemo(() => {
    const tk = data?.tokens;
    if (!tk) return null;
    const limitReached = isLimitReached(tk.used ?? 0, tk.limit ?? 0);
    return {
      used: tk.used ?? 0,
      limit: tk.limit ?? 0,
      color: getUsageColor(tk.used ?? 0, tk.limit ?? 0),
      percentage: getUsagePercentage(tk.used ?? 0, tk.limit ?? 0),
      remaining: Math.min(
        tk.remaining ?? getRemaining(tk.used ?? 0, tk.limit ?? 0),
        getRemaining(tk.used ?? 0, tk.limit ?? 0)
      ),
      limitReached,
      // TD-2119 R3: explicit alias for token-pool exhaustion. Semantically
      // identical to limitReached for tokens (isLimitReached already returns
      // false when limit <= 0), but lets gating call sites read clearly as
      // `tokens.totalExhausted` rather than re-checking the limit defensively.
      totalExhausted: limitReached,
      fallbackActive: data.fallback_active ?? false,
      // TD-2443: BE-owned "show this user the popup this period" flags. Each is
      // true only when the state is active AND the user has not yet been notified
      // for the current period; on-hold takes precedence over fallback (the BE
      // returns fallback_notify=false whenever on hold). The FE obeys these
      // instead of the old per-browser-session sessionStorage gate.
      fallbackNotify: data.fallback_notify ?? false,
      onholdNotify: data.onhold_notify ?? false
    };
  }, [data]);

  return {
    data,
    subscribed: data?.subscribed ?? false,
    expired: data?.expired ?? false,
    companyName: data?.company_name ?? null,
    subscriptionExpiry: data?.subscription_expiry ?? null,
    contractStart: data?.contract_start ?? null,
    downloads,
    tokens,
    enrichment,
    period,
    isLoading,
    error: snapshot.error,
    refetch,
    ackNotification
  };
};

export default useSubscriptionStatus;
